'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  applyBasketChanges,
  clearBasket,
} from '~/actions/basket/basket-actions';
import {
  OrderSimulationStatus,
  type components,
  type Website,
} from '~/fecom-api/schema';
import { useAction } from 'next-safe-action/hooks';
import { usePathname } from 'next/navigation';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { trpc } from '~/trpc/client';
import {
  gtmBasketChanges,
  gtmDataLayerEvent,
  mapProductToGtmItem,
} from '~/utils/gtm/gtm-helpers';
import { type LinkPickerValue } from '~/utils/content-api-utils/content-helpers';

export type BasketChange = {
  type: 'product';
  itemCode: string;
  quantity: number;
};
// | {
//     type: 'alternative';
//     itemCode: string;
//     newItemCode: string;
//   };

export type CoreBasketModel = {
  isAuthenticated?: boolean;
  basket?: components['schemas']['Basket'] | null;
  products?: components['schemas']['ProductDetails'][];
  changes?: BasketChange[];
  taskId?: string | null;
  discontinuedProducts?: components['schemas']['RemovedProduct'][];
  user?: {
    email?: string | null;
    fullName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    showExportFms?: boolean;
  };
  features?: {
    disableCheckoutButtonForPunchout?: boolean;
    isPunchoutSite?: boolean;
    isPunchoutUser?: boolean;
  };
  content?: {
    inStockDeliveryMessage?: string | null;
    smartOrderPdfLink?: LinkPickerValue | null;
  };
  website?: Website;
};

export type BasketChangesModel = {
  changes?: BasketChange[];
};

type ReloadBasketParams = {
  runOrderSimulation?: boolean;
  forceInitialLoadingState?: boolean;
  resetBasketId?: boolean;
};

type ApplyBasketChangesParams = {
  hideToast?: boolean;
};

type BasketContextModel = CoreBasketModel & {
  actions?: {
    reload: (params?: ReloadBasketParams) => Promise<void>;
    update: (itemCode: string, quantity: number) => void;
    remove: (itemCode: string) => void;
    apply: (params?: ApplyBasketChangesParams) => Promise<void>;
    clear: () => Promise<void>;
    undo: (itemCode: string) => void;
  };
  error?: string;
  isInitialLoading: boolean;
  isProcessing: boolean;
};

const BasketContext = createContext<BasketContextModel>({
  isInitialLoading: true,
  isProcessing: false,
});

type BasketProviderProps = {
  initialBasket: CoreBasketModel | null;
  children: React.ReactNode;
};

export function BasketProvider({
  children,
  initialBasket,
}: BasketProviderProps) {
  const pathname = usePathname();

  const [basketData, dispatch] = useReducer(changesReducer, initialBasket);

  // we want to show a different loader when the basket initially loaded
  // or occasionally force it when the basket is reloaded
  const runCount = useRef(0);

  // Note: we can't use a query here because we need to pass the taskId to the order simulate endpoint
  const basketMutation = trpc.basket.getBasket.useMutation({
    onError: (error) => {
      console.error(error);
      setIsProcessing(false);
    },
    onSuccess: async (data) => {
      if (!data) {
        return;
      }
      if (!data.taskId) {
        setIsProcessing(false);
      }
      dispatch({ type: 'set', basket: data });
      runCount.current += 1;
    },
  });

  const [lastTaskId, setLastTaskId] = useState<string | null>(null);
  useEffect(() => {
    if (basketData?.taskId) {
      setLastTaskId(basketData.taskId);
    }
  }, [basketData?.taskId]);

  // ! make an order simulate query that will ping the endpoint when the basket has a task ID
  // ! and when the response is 'pending' or 'running'
  const orderSimulateStatus = trpc.basket.orderSimulate.useQuery(
    { taskId: lastTaskId ?? '' },
    {
      enabled: !!lastTaskId,
      refetchInterval(query) {
        if (query.state.data?.status === OrderSimulationStatus.InProgress) {
          return 1000;
        }
        return false;
      },
    },
  );

  useEffect(() => {
    if (orderSimulateStatus.data?.status === OrderSimulationStatus.Failed) {
      setIsProcessing(false);
    }
  }, [orderSimulateStatus.data?.status]);

  const isOrderSimulateInProgress =
    orderSimulateStatus.isFetching ||
    orderSimulateStatus.isLoading ||
    orderSimulateStatus.data?.status === OrderSimulationStatus.InProgress;

  const [isProcessing, setIsProcessing] = useState(true);

  // split this out so that the basket can be reloaded without the need to re-render the entire component
  const mutateBasket = basketMutation.mutateAsync;

  const trpcUtils = trpc.useUtils();

  const handleReload = useCallback(
    async (params: ReloadBasketParams | undefined) => {
      try {
        setIsProcessing(true);
        if (params?.forceInitialLoadingState) {
          runCount.current = 0; // we want to see the loading skeleton in this scenario
        }

        runCount.current += 1;
        await mutateBasket({
          runOrderSimulation: params?.runOrderSimulation ?? false,
          resetBasketId: params?.resetBasketId ?? false,
        });
      } catch (error) {
        throw new Error('Failed to reload basket');
      }

      setApplyChangesToastId(null);
    },
    [mutateBasket],
  );

  // run the reload once when the component mounts
  useEffect(() => {
    if (runCount.current) {
      return;
    }

    const go = async () => {
      await handleReload({
        resetBasketId: false,
        runOrderSimulation: pathname === '/basket',
      });
    };
    void go();
  }, [basketData?.basket?.basketId, handleReload, pathname]);

  {
    // reload basket when order simulate completes

    const reloadBasket =
      (orderSimulateStatus.data?.status === OrderSimulationStatus.Completed ||
        orderSimulateStatus.data?.status === OrderSimulationStatus.Failed) &&
      orderSimulateStatus.data.taskId === basketData?.taskId;
    !basketMutation.isPending;

    useEffect(() => {
      // Get the basket again if the order simulation is completed
      if (reloadBasket) {
        void handleReload({ runOrderSimulation: false });
      }
    }, [handleReload, reloadBasket]);
  }

  {
    // reload basket when user navigates to basket page
    const previousPage = useRef(pathname);
    const currentPage = pathname;

    useEffect(() => {
      if (currentPage !== previousPage.current) {
        previousPage.current = currentPage;
        if (currentPage === '/basket') {
          void handleReload({ runOrderSimulation: true });
        }
      }
    }, [currentPage, handleReload]);
  }

  const updateBasket = useAction(applyBasketChanges);

  const clearBasketApi = useAction(clearBasket);

  const [applyChangesToastId, setApplyChangesToastId] = useState<
    'apply-changes-toast' | null
  >(null);

  const handleApply = useCallback(
    async (params?: ApplyBasketChangesParams) => {
      try {
        if (!basketData?.changes || basketData.changes.length === 0) {
          return;
        }
        setIsProcessing(true);
        gtmBasketChanges({
          rows: basketData?.basket?.rows ?? [],
          changes: basketData?.changes ?? [],
          products: basketData?.products ?? [],
        });
        const updatePromise = updateBasket
          .executeAsync({
            items:
              basketData?.changes?.map((change) => ({
                itemCode: change.itemCode,
                quantity: change.quantity,
              })) ?? [],
          })
          .then(async () => {
            await handleReload({ runOrderSimulation: true });
            setApplyChangesToastId(null);
            dispatch({ type: 'clear-changes' });
          });
        if (params?.hideToast) {
          await updatePromise;
          return;
        }
        await toast
          .promise(updatePromise, {
            loading: 'Applying changes to basket...',
            success: 'Changes applied to basket',
            error: 'Failed to apply changes to basket',
          })
          .unwrap();
      } catch (error) {
        if (error instanceof Error) {
          console.error(error.message);
        }
      }
    },
    [
      basketData?.basket?.rows,
      basketData?.changes,
      basketData?.products,
      handleReload,
      updateBasket,
    ],
  );

  const isAnythingProcessing = isProcessing;
  !!trpcUtils.basket.addToBasket.isMutating() ||
    !!trpcUtils.basket.addToBasket.isMutating();

  // show toast when changes are present and user is on basket page
  useEffect(() => {
    if (applyChangesToastId && pathname === '/basket') {
      toast.error(`Update basket to continue`, {
        className: 'flex gap-4',
        id: 'apply-changes-toast',
        duration: Infinity,
        dismissible: false,
        position: 'top-center',
        action: (
          <Button
            onClick={() => handleApply()}
            size="sm"
            variant="destructive"
            loader={isProcessing}
            disabled={isProcessing}
            className="ml-auto"
          >
            Update
          </Button>
        ),
      });
    } else {
      toast.dismiss('apply-changes-toast');
    }
  }, [
    applyChangesToastId,
    basketData?.changes?.length,
    handleApply,
    isProcessing,
    pathname,
  ]);

  // show toast when changes are present
  useEffect(() => {
    if (basketData?.changes && basketData.changes?.length > 0) {
      setApplyChangesToastId('apply-changes-toast');
    } else {
      setApplyChangesToastId(null);
    }
  }, [basketData?.changes]);

  function getErrorMessage() {
    if (
      orderSimulateStatus.isError ||
      orderSimulateStatus.data?.status === OrderSimulationStatus.Failed
    ) {
      return 'Failed to simulate order';
    }
    if (basketMutation.isError) {
      return 'Failed to fetch basket';
    }
  }

  const error = getErrorMessage();

  const handleClear = useCallback(async () => {
    dispatch({ type: 'clear-changes' });
    await toast
      .promise(
        clearBasketApi.executeAsync().then(async () => {
          runCount.current = 0;
          gtmDataLayerEvent({
            event: 'remove_from_cart',
            ecommerce: {
              items:
                basketData?.basket?.rows.map((row) => {
                  const product = basketData?.products?.find(
                    (p) => p.itemCode === row.itemCode,
                  );
                  if (!product) {
                    return {
                      item_id: row.itemCode,
                      item_ref: row.itemCode, // note: we do note have the cat ref here if there is no product
                      quantity: row.quantity,
                    };
                  }
                  return mapProductToGtmItem({
                    product,
                    quantity: row.quantity,
                  });
                }) ?? [],
            },
          });
          await handleReload({
            forceInitialLoadingState: true,
            runOrderSimulation: true,
            resetBasketId: true,
          });
          setApplyChangesToastId(null);
        }),
        {
          loading: `Emptying basket...`,
          success: 'Basket emptied',
          error: `Failed to empty basket`,
        },
      )
      .unwrap();
  }, [clearBasketApi, handleReload]);

  const handleRemove = useCallback((itemCode: string) => {
    dispatch({ type: 'remove', itemCode });
  }, []);

  const handleUpdate = useCallback((itemCode: string, quantity: number) => {
    dispatch({ type: 'update', itemCode, quantity });
  }, []);

  const handleUndo = useCallback((itemCode?: string) => {
    if (!itemCode) {
      return;
    }
    dispatch({ type: 'undo', itemCode });
  }, []);

  const actions = useMemo((): BasketContextModel['actions'] => {
    return {
      apply: handleApply,
      clear: handleClear,
      reload: handleReload,
      remove: handleRemove,
      undo: handleUndo,
      update: handleUpdate,
    };
  }, [
    handleApply,
    handleClear,
    handleReload,
    handleRemove,
    handleUndo,
    handleUpdate,
  ]);

  const isInitialLoading = basketData === null && !basketMutation.isError;

  const basketContextValue: BasketContextModel = useMemo(() => {
    const basket = basketData?.basket;
    const products = basketData?.products;

    return {
      basket,
      products,
      discontinuedProducts:
        orderSimulateStatus.data?.discontinuedProducts ?? [],
      isAuthenticated: basketData?.isAuthenticated,
      user: basketData?.user,
      features: basketData?.features,
      content: basketData?.content,
      isOrderSimulateInProgress,
      changes: basketData?.changes,
      actions,
      error,
      isInitialLoading,
      isProcessing: isAnythingProcessing,
      website: basketData?.website,
    };
  }, [
    basketData?.basket,
    basketData?.products,
    basketData?.isAuthenticated,
    basketData?.user,
    basketData?.features,
    basketData?.content,
    basketData?.changes,
    basketData?.website,
    orderSimulateStatus.data?.discontinuedProducts,
    isOrderSimulateInProgress,
    actions,
    error,
    isInitialLoading,
    isAnythingProcessing,
  ]);

  return (
    <BasketContext.Provider value={basketContextValue}>
      {children}
    </BasketContext.Provider>
  );
}

export const useBasket = () => {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error('useBasket must be used within a BasketProvider');
  }
  return context;
};

export { BasketContext };

type BasketSetAction = { type: 'set'; basket: CoreBasketModel };
type BasketUpdateAction = {
  type: 'update';
  itemCode: string;
  quantity: number;
};
type BasketRemoveAction = { type: 'remove'; itemCode: string };
type BasketUndoAction = { type: 'undo'; itemCode: string };
type BasketClearChangesAction = { type: 'clear-changes' };

type BasketChangesAction =
  | BasketSetAction
  | BasketUpdateAction
  | BasketRemoveAction
  | BasketUndoAction
  | BasketClearChangesAction;

function changesReducer(
  data: CoreBasketModel | null,
  action: BasketChangesAction,
): CoreBasketModel | null {
  switch (action.type) {
    case 'set': {
      return {
        ...action.basket,
        changes: [],
        products: action.basket.products?.slice(),
      };
    }
    case 'clear-changes': {
      return { ...data, changes: [] };
    }
    case 'update': {
      return updateBasket(data, action);
    }
    case 'remove': {
      return removeBasketItem(data, action);
    }
    case 'undo': {
      return {
        ...data,
        changes: data?.changes?.filter((i) => i.itemCode !== action.itemCode),
      };
    }
    default:
      return data;
  }
}

function updateBasket(
  data: CoreBasketModel | null,
  action: BasketUpdateAction,
): CoreBasketModel | null {
  // change already exists
  const change = data?.changes?.find((i) => i.itemCode === action.itemCode);
  const row = data?.basket?.rows?.find((i) => i.itemCode === action.itemCode);

  // but doesn't result in quantity change
  if (action.quantity === row?.quantity) {
    return {
      ...data,
      changes: data?.changes?.filter((i) => i.itemCode !== action.itemCode),
    };
  }

  // and does result in quantity change
  if (change) {
    return {
      ...data,
      changes: data?.changes?.map((change) => {
        if (change.type === 'product' && change.itemCode === action.itemCode) {
          return { ...change, quantity: action.quantity };
        }
        return change;
      }),
    };
  }

  // change does not exist
  return {
    ...data,
    changes: [
      ...(data?.changes ?? []),
      {
        type: 'product',
        itemCode: action.itemCode,
        quantity: action.quantity,
      },
    ],
  };
}

function removeBasketItem(
  data: CoreBasketModel | null,
  action: BasketRemoveAction,
): CoreBasketModel | null {
  if (data?.changes?.find((i) => i.itemCode === action.itemCode)) {
    return {
      ...data,
      changes: data?.changes?.map((change) => {
        if (change.itemCode === action.itemCode) {
          return {
            ...change,
            quantity: 0,
          };
        }
        return change;
      }),
    };
  }

  // change does not exist - add it
  return {
    ...data,
    changes: [
      ...(data?.changes ?? []),
      {
        type: 'product',
        itemCode: action.itemCode,
        quantity: 0,
      },
    ],
  };
}
